import {Directive, ElementRef, input, output, signal, TemplateRef, ViewContainerRef} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {exhaustMap, fromEvent, merge, Observable, of, switchMap, takeUntil, tap, throttle, timer, race} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {eventStopOgPrevent, fileListSomArray} from "felles-ng2/støttefunksjoner/dom";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {TemplatePortal} from "@angular/cdk/portal";
import {leverForsinketMedMindreAnnenStrømLeverer} from "felles-ng2/støttefunksjoner/observable";

const debug = initDebugger('DraOgSlippFilDirektiv');

/**
 * Direktiv som lytter til dra-og-slipp fil hendelser.
 *
 * Eks: `<div [draOgSlippFil]="vedFilSluppet($event)"></div>`
 */
@Directive({
    selector: '[draOgSlippFil]',
    standalone: true
})
export class DraOgSlippFilDirektiv {

    /** Event som fyres når filer har blitt slupper, $event er array med filer */
    vedFilSluppet = output<File[]>({alias: 'draOgSlippFil'});
    /** Event som fyres når dra-og-slipp har blitt aktivert ({@liknk #meldingVedAktivert} blir vist), eller deaktivert ({@liknk #meldingVedAktivert} blir skjult), $event er true dersom dra-og-slipp er aktivert, false dersom deaktivert */
    vedDraOgSlippAktivert = output<boolean>({alias: 'draOgSlippAktivert'});

    /** Melding som vises i overlay når bruker drar fil over nettleservinduet */
    meldingVedAktivert = input.required<TemplateRef<ElementRef>>()
    /** Melding som vises i overlay når bruker drar fil over nettleservinduet og {@link #draOgSlippStøttet} returnerer false */
    meldingVedIkkeStoettet = input.required<TemplateRef<ElementRef>>()
    /** Referanse til metode som returnerer false dersom vi ikke for øyeblikket støtter dra-og-slipp av filer */
    draOgSlippStøttet = input<DraOgSlippFilStøttet>(() => true, {alias: 'draOgSlippStoettet'});

    private draOgSlipFilAktivert = signal(false);
    private overlayRef: OverlayRef | null = null;

    constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {
        this.håndterDraOgSlippFilopplasting();
    }

    //private hjelpemetoder

    private håndterDraOgSlippFilopplasting() {
        //merk at dragover fyres kontinuerlig (med omkring 100ms intervaller) så lenge bruker har dratt fil over nettleservinduet, uavhengig av om bruker beveger musa eller ei
        const draBegynt = this.lyttTilHendelse('dragover')
        const filSluppet = this.lyttTilHendelse('drop')
        //leverer dragleave kun dersom det ikke kommer en dragover event ila de neste 100ms
        const draAvsluttet = this.lyttTilHendelse('dragleave')
          .pipe(leverForsinketMedMindreAnnenStrømLeverer(100, draBegynt), takeUntilDestroyed());

        draBegynt
          //leverer første draBegynt-event, undertrykker så påfølgende inntil vi får en filSluppet- eller draAvsluttet-event
          .pipe(throttle(() => race(filSluppet, draAvsluttet)))
          .subscribe(draBegynt => this.draFilStartet());
        filSluppet.subscribe(filSluppet => this.filSluppet(fileListSomArray(filSluppet.dataTransfer?.files)));
        draAvsluttet.subscribe(draAvsluttet => this.draFilAvsluttet());
    }

    private lyttTilHendelse(hendelse: string): Observable<DragEvent> {
        return fromEvent<DragEvent>(window, hendelse)
          .pipe(
            tap(e => eventStopOgPrevent(e)),
            //tap(e => debug(`draOgSlipp-hendelse ${e.type}`, e)),
            takeUntilDestroyed()
          );
    }

    private draFilStartet() {
        if (!this.draOgSlippFilStøttetOgVisMeldingDersomIkke()) return;
        this.visDraOgSlippFilIndikator();
        this.draOgSlipFilAktivert.set(true);
        this.vedDraOgSlippAktivert.emit(true);
    }

    private draOgSlippFilStøttetOgVisMeldingDersomIkke(): boolean {
        if (this.erDraOgSlippStøttet()) return true;
        this.visDraOgSlippFilIndikator(false);
        return false;
    }

    private erDraOgSlippStøttet() {
        return this.draOgSlippStøttet()();
    }

    private visDraOgSlippFilIndikator(draOgSlippStøttet = true) {
        if (erIkkeNil(this.overlayRef)) return;
        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'dra-og-slipp-fil-aktivert-bakgrunn',
            panelClass: ['dra-og-slipp-fil-aktivert-melding'],
            maxWidth: 400,
            scrollStrategy: this.overlay.scrollStrategies.close(),
            positionStrategy: this.overlay
              .position()
              .global()
              .centerHorizontally()
              .centerVertically()
        });

        this.overlayRef.attach(new TemplatePortal(
          draOgSlippStøttet ? this.meldingVedAktivert() : this.meldingVedIkkeStoettet(),
          this.viewContainerRef
        ));
    }

    private async filSluppet(filer: File[]) {
        this.draFilAvsluttet();
        if (!this.erDraOgSlippStøttet()) return;
        this.vedFilSluppet.emit(filer);
    }

    private draFilAvsluttet() {
        this.draOgSlipFilAktivert.set(false);
        this.skjulDraOgSlippFilIndikator();
        this.vedDraOgSlippAktivert.emit(false);
    }

    private skjulDraOgSlippFilIndikator() {
        this.overlayRef?.detach();
        this.overlayRef = null;
    }

}

export type DraOgSlippFilStøttet = () => boolean;
